import { useCallback, useMemo } from 'react'

import { PromoCode } from 'lib/components/AdminEditOrderFooter/PromoCodeInput'
// eslint-disable-next-line max-len
import { PackageAndProtectionCoverageOption } from 'lib/components/PackageAndProtection/PackageAndProtectionItem'

import useCartDiscountRemoveApi from '@/app/book/api/useCartDiscountRemoveApi'
import useCartGiftCardRemoveApi from '@/app/book/api/useCartGiftCardRemoveApi'
import {
  CartItemCreateInputType,
  CartItemInputType,
  DirectionType,
} from '@/app/book/api/useCartItemCreateApi'
import type { BookingFlowProduct, TravelerDetailsItemData } from '@/app/book/stores/useCartStore'
import useCartStore from '@/app/book/stores/useCartStore'
import useShipmentStore from '@/app/book/stores/useShipmentStore'
import { useCartUser } from '@/hooks/useCartUser'
import { usePricingExperiments } from '@/hooks/usePricingExperiments'
import useAnonymousIdStore from '@/stores/useAnonymousIdStore'
import { Cart } from '@/types/cart'
import { ShipRoute } from '@/types/order'
import type { ShipmentDatesSegment } from '@/types/shipment'
import { convertToISO8601 } from '@/utils/dateHelpers'

import useCartUpsertApi, { UpsertCartInput } from '../api/useCartUpsertApi'

import useProductLineOptions from './useProductLineOptions'

const PICKUP_FEE_CATEGORY = 'pickup_fee'
const DROPOFF_ID = 'dropoff'

// Lookup helpers
const findPackagingIdFromProduct = (product: BookingFlowProduct): string | undefined =>
  product.packagingTypes?.find(({ id }) => id === product.packagingType)?.id

const findInsuranceIdFromProduct = (product: BookingFlowProduct): string | undefined =>
  product?.coverageOptions?.find(({ id }) => id === product?.insuranceId)?.id

const findServiceRateIdFromProductAndLeg = (
  product: BookingFlowProduct,
  leg: ShipmentDatesSegment,
): string => leg.items?.find(({ productId }) => productId === product.id)?.serviceRateId || ''

const findTravelerOrDefault = (
  travelers: TravelerDetailsItemData[],
  product: BookingFlowProduct,
): TravelerDetailsItemData => {
  const traveler = travelers.find((traveler) => traveler.name === product.traveler) || travelers[0]

  return {
    id: traveler?.id || undefined,
    name: traveler?.name || 'Placeholder Name',
    email:
      traveler?.email ||
      'this-is-a-longer-placeholder-that-should-not-collide@example-domain-long-form-20250922.com',
    phone: traveler?.phone || '12345678900',
  }
}

const findFeesFromLeg = (leg: ShipmentDatesSegment): { feeId: string; feeCategory: string }[] => {
  return leg.handlingOptionId && leg.handlingOptionId !== DROPOFF_ID
    ? [{ feeId: leg.handlingOptionId, feeCategory: PICKUP_FEE_CATEGORY }]
    : []
}

// Mapping helpers

const generateCartItemInput =
  (travelers: TravelerDetailsItemData[], leg: ShipmentDatesSegment) =>
  (product: BookingFlowProduct): CartItemInputType => ({
    traveler: findTravelerOrDefault(travelers, product),
    productId: product.id,
    productLineId: product.product_line.id,
    insuranceId: findInsuranceIdFromProduct(product),
    serviceRateId: findServiceRateIdFromProductAndLeg(product, leg),
    additionalInfo: product.additionalInfo,
    // TODO: discountId: '',
    packagingId: findPackagingIdFromProduct(product),
    fees: findFeesFromLeg(leg),
    labelId: product.id, // TODO: determine diff between productId and labelId
  })

const generateInput = (
  cartToken: string,
  products: BookingFlowProduct[],
  shipRoute: ShipRoute,
  travelers: TravelerDetailsItemData[],
  leg: ShipmentDatesSegment,
  index: number,
): CartItemCreateInputType => {
  const defaultPhoneNumber = travelers[0]?.phone || '12345678900'
  const defaultAttentionName = travelers[0]?.name || 'Placeholder Name'

  const arrivalDate = leg.arrivalDate || ''
  const shipDate = leg.shipDate || ''

  const destination = {
    ...shipRoute.destination,
    attentionName: shipRoute.destination.attentionName || defaultAttentionName,
    phoneNumber: shipRoute.destination.phoneNumber || defaultPhoneNumber,
  }

  const origin = {
    ...shipRoute.origin,
    attentionName: shipRoute.origin.attentionName || defaultAttentionName,
    phoneNumber: shipRoute.origin.phoneNumber || defaultPhoneNumber,
  }

  return {
    cartToken,
    shipRoute: {
      origin: index === 0 ? origin : destination,
      destination: index === 0 ? destination : origin,
    }, // TODO: cruise and facility id
    shipDate: shipDate ? convertToISO8601(shipDate) : '',
    arrivalDate: arrivalDate ? convertToISO8601(arrivalDate) : '',
    dropOff: leg.handlingOptionId === DROPOFF_ID,
    direction: index === 0 ? DirectionType.Outbound : DirectionType.Inbound,
    items: products.map(generateCartItemInput(travelers, leg)),
  }
}

export type UpdateCart = (params?: Partial<CartParams>) => Promise<Cart | undefined>

// Cart Hook
interface UseCart {
  cart: Cart | null
  error: Error | null
  updateCart: UpdateCart
  refreshCart: (cart: Cart) => void
  removePromo: (promoCode: PromoCode) => void
  promoCodes: PromoCode[]
  isPending?: boolean
  setIsPending: (isPending: boolean) => void
}

interface CartParams {
  products: BookingFlowProduct[]
  shipRoute: ShipRoute
  shipmentDates: ShipmentDatesSegment[]
  travelers: TravelerDetailsItemData[]
  paymentMethodType?: string
}

// global flag to track cart creation to get around multiple mounts
export const useCart = (): UseCart => {
  const isLoading = useCartStore((state) => state.isLoading)
  const setIsLoading = useCartStore((state) => state.setIsLoading)
  const acceptedNotes = useCartStore((state) => state.acceptedNotes)
  const initRoute = useShipmentStore((state) => state.initRoute)
  const selectedProducts = useCartStore((state) => state.selectedProducts)
  const shipment = useShipmentStore((state) => state.shipment)
  const travelers = useCartStore((state) => state.travelers)
  const impersonatingUser = useCartStore((store) => store.impersonatingUser)
  const { user } = useCartUser()

  const storedCart = useCartStore((state) => state.cart)
  const setStoredCart = useCartStore((state) => state.setCart)
  const { mutateAsync: removeCartDiscountMutation } = useCartDiscountRemoveApi()

  const { mutateAsync: removeCartGiftCardMutation } = useCartGiftCardRemoveApi()
  const { getDefaultCoverageOption, getCoverageOptions } = useProductLineOptions()

  const {
    mutateAsync: upsertCartMutation,
    error: upsertCartError,
    isPending: isUpserting,
  } = useCartUpsertApi()

  const refreshCart = useCallback(
    (cart: Cart) => {
      setStoredCart(cart)
    },
    [setStoredCart],
  )

  // Main cart resolution: stored cart from cart store takes priority, once it has items
  // then generated cart
  const cart = useMemo(() => {
    if (!storedCart?.orderSummary?.legs?.length) return null

    return {
      ...storedCart,
      orderSummary: {
        ...storedCart.orderSummary,
        legs: storedCart?.orderSummary?.legs?.map((leg) => ({
          ...leg,
          travelers: leg?.travelers?.map((traveler) => ({
            ...traveler,
            name: traveler.name !== 'Placeholder Name' ? traveler.name : user?.name || '',
          })),
        })),
      },
    }
  }, [storedCart, user?.name])

  const removeGiftCard = useCallback(
    async (promoCode: PromoCode): Promise<void> => {
      if (!storedCart?.cartToken || !promoCode.id) return

      const result = await removeCartGiftCardMutation({
        cartToken: storedCart.cartToken,
        id: promoCode.id!,
      })
      if (result?.cart) {
        refreshCart(result.cart)
      }
    },
    [storedCart, removeCartGiftCardMutation, refreshCart],
  )

  const removePromo = useCallback(
    async (promoCode: PromoCode): Promise<void> => {
      if (!cart?.cartToken) return

      if (promoCode.promoType === 'giftCard' || promoCode.promoType === 'accountCredit') {
        return removeGiftCard(promoCode)
      }

      const result = await removeCartDiscountMutation({ cartToken: cart?.cartToken })
      if (result?.cart) {
        refreshCart(result.cart)
      }
    },
    [cart, removeCartDiscountMutation, removeGiftCard, refreshCart],
  )

  // combine the coupons and gift cards into a single array of promo codes
  const promoCodes = useMemo(() => {
    if (!cart) return []
    const coupons =
      (cart.coupons?.map((promoCode) => ({
        promoCode: promoCode.coupon.code || '',
        discountCents: promoCode.amountCents,
        promoType: (promoCode.coupon.code || '') === 'COMPDISCOUNT' ? 'comp' : 'coupon',
      })) as PromoCode[]) || []
    const giftCards =
      (cart.giftCards?.map((giftCard) => ({
        promoCode: giftCard.giftCard.displayCode || '',
        discountCents: giftCard.amountCents,
        promoType: 'giftCard',
        id: giftCard.giftCard.id,
      })) as PromoCode[]) || []
    return [...coupons, ...giftCards]
  }, [cart])

  const couponCode = useMemo(
    () => promoCodes.find((promoCode) => promoCode.promoType === 'coupon')?.promoCode,
    [promoCodes],
  )

  const giftCardIds = useMemo(
    () =>
      promoCodes
        .filter((promoCode) => promoCode.promoType === 'giftCard')
        .flatMap((giftCard) => (giftCard.id ? [giftCard.id] : [])),
    [promoCodes],
  )

  const generateInputs = useCallback(
    (params?: CartParams) =>
      (params?.shipmentDates || shipment?.shipment_dates || []).map((leg, index) =>
        generateInput(
          cart?.cartToken || '',
          params?.products || selectedProducts,
          params?.shipRoute || shipment?.shipRoute || initRoute(),
          params?.travelers || travelers,
          leg,
          index,
        ),
      ),
    [cart, initRoute, selectedProducts, shipment.shipRoute, shipment.shipment_dates, travelers],
  )

  const { getVariationId, getVariationCampaignId } = usePricingExperiments()

  const variationId = getVariationId()
  const variationCampaignId = getVariationCampaignId()

  // Function to upsert the cart
  // This function will create or update the cart based on the provided parameters
  // If no parameters are provided, it will use the current cart state
  // If incomplete parameters are provided, it will fall back to the current cart state
  // It will also handle the case where the cart is being created for the first time
  // and will set the cart token in the store
  // It returns a promise that resolves to the updated cart or undefined if the operation fails
  const upsertCart = useCallback(
    async (params?: Partial<CartParams>) => {
      const shipmentDates = params?.shipmentDates || shipment?.shipment_dates

      // cannot create a cart without shipment dates
      if (
        !shipmentDates ||
        !shipmentDates.length ||
        !shipmentDates.every(
          ({ arrivalDate, service, shipDate }) => (arrivalDate || shipDate) && service,
        )
      )
        return

      let products = params?.products || selectedProducts
      const shipRoute = params?.shipRoute || shipment?.shipRoute || initRoute()
      const inputTravelers = params?.travelers || travelers

      products = products.map((product) => {
        if (!product.coverageOptions || product.coverageOptions.length === 0) {
          const coverageOptions = getCoverageOptions(product.product_line.id)
          const defaultCoverageOption = getDefaultCoverageOption(coverageOptions)
          const formattedCoverageOptions = coverageOptions.map((option) => ({
            title: option.humanReadableFormat,
            value: option.coverageAmountCents,
            ...option,
          })) as PackageAndProtectionCoverageOption[]

          return {
            ...product,
            coverageOptions: formattedCoverageOptions,
            insuranceId: product.insuranceId || defaultCoverageOption?.id,
          } as BookingFlowProduct
        }
        return product
      })

      const updatedShipRoute = {
        origin: {
          ...shipRoute.origin,
          address1: shipRoute.origin.address1 || 'Placeholder Address',
          facilityId: shipment?.recognized_places[0]?.id,
        },
        destination: {
          ...shipRoute.destination,
          address1: shipRoute.destination.address1 || 'Placeholder Address',
          facilityId: shipment?.recognized_places[1]?.id,
        },
      }

      const input: UpsertCartInput = {
        cartToken: cart?.cartToken,
        device: 'web',
        anonymousId: useAnonymousIdStore.getState().anonymousId ?? undefined,
        items: generateInputs({
          products,
          shipRoute: updatedShipRoute,
          shipmentDates,
          travelers: inputTravelers,
        }),
        noteAcceptance: acceptedNotes,
      }

      if (impersonatingUser) {
        input.userId = impersonatingUser.id
      }

      if (variationId) {
        input.experimentVariationId = variationId
      }

      if (variationCampaignId) {
        input.experimentId = variationCampaignId
      }

      if (params?.paymentMethodType) {
        let paymentMethodType = 'CREDIT_CARD'

        if (params.paymentMethodType === 'clubBilling') {
          paymentMethodType = 'CLUB_BILLING'
        }

        input.paymentMethodType = paymentMethodType
      }

      if (couponCode) input.couponCode = couponCode

      if (giftCardIds.length > 0) {
        input.giftCardIds = giftCardIds as string[]
      }

      setIsLoading(true)
      return upsertCartMutation(input, {
        onSuccess: ({ cart: newCart }) => {
          if (newCart) setStoredCart(newCart || null)
        },
        onSettled: () => {
          setIsLoading(false)
        },
      })
    },
    [
      shipment?.shipment_dates,
      shipment?.shipRoute,
      shipment?.recognized_places,
      selectedProducts,
      initRoute,
      travelers,
      cart?.cartToken,
      generateInputs,
      acceptedNotes,
      couponCode,
      giftCardIds,
      impersonatingUser,
      variationId,
      variationCampaignId,
      upsertCartMutation,
      getCoverageOptions,
      getDefaultCoverageOption,
      setStoredCart,
      setIsLoading,
    ],
  )

  // UseCart Data
  return {
    cart: cart || null,
    updateCart: upsertCart,
    error: upsertCartError || null,
    refreshCart,
    removePromo: removePromo,
    promoCodes: promoCodes,
    isPending: isLoading || isUpserting,
    setIsPending: setIsLoading,
  }
}
